import { useTranslation } from 'react-i18next'
import './index.scss'
import { useContext, useEffect, useMemo, useState } from 'react'
import Input from '../../components/Input'
import UserContext from '../../contexts/UserContext/context'
import { useQuery } from 'react-query'
import { getMobileList } from '../../services/calls/mobiles'
import Button from '../../components/Button'
import Table from '../../components/Table'
import { MAINTENANCE_COL_IDS, MAINTENANCE_COLUMNS } from './table/data'
import StatusColumn from './table/StatusColumn'
import CarMaintenance from './CarMaintenance'
import { getMaintenanceBase } from '../../services/calls/maintenance'

const Maintenance = () => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const [mobiles, setMobiles] = useState<string[]>([])
  const [fleets, setFleets] = useState<number[]>([])

  const mobileParams = useMemo(() => {
    if (!fleets) return
    return { fleetid: fleets }
  }, [fleets])

  const { data, refetch, isLoading } = useQuery('getMaintenanceBase', () => getMaintenanceBase({ mobileid: mobiles })) 
  const { data: mobileData } = useQuery([mobileParams], getMobileList)
  const { data: fullMobileList } = useQuery([], getMobileList)

  const mobileOptions = useMemo(() => {
    const options = {}
    if (fullMobileList) {
      fullMobileList?.forEach((mobile) => {
        options[mobile.id] = mobile.globalid
      })
    }
    return options
  }, [fullMobileList])

    useEffect(() => {
      if (fleets.length && mobileData) {
        const mobileIds = mobileData.map((mobile) => mobile.id)
        setMobiles(mobileIds)
      }
    }, [mobileData])

  const fleetOptions = useMemo(() => {
    const options: Record<number, string> = {}
    if (user) {
      user.available_fleets.forEach((fleet) => {
        options[fleet.fleetid] = fleet.name
      })
    }
    return options
  }, [user])

  const mappedHeader = useMemo(() => {
    const lookup = {}
    Object.keys(MAINTENANCE_COLUMNS).forEach((key, index) => {
      const col = MAINTENANCE_COLUMNS[key]
      const { id } = col
      lookup[id] = {
        ...col,
        index,
        label: t(col.label),
        formatFunction: (val, row) => {
          if (key === MAINTENANCE_COL_IDS.STATUS) {
            return (
              <StatusColumn status={val} />
            )
          } else if (key === MAINTENANCE_COL_IDS.ODOMETER) {
            return val?.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
          }
          return val
        }
      }
    })
    return lookup
  }, [t])

  const rows = useMemo(() => {
    const rowArr = []
    if (data) {
      data.forEach((d) => {
        const newRow = {
          ...d,
          expandElem: <CarMaintenance mobile={d} />
        }
        Object.keys(mappedHeader).map((id) => {
          const { path } = mappedHeader[id]
          const value = d[path]
          newRow[id] = value
        })
        rowArr.push(newRow)
      })
    }
    return rowArr
  }, [mappedHeader, data])

  const clearFilters = () => {
    setMobiles([])
    setFleets([])
  }

  return (
    <div className="Maintenance">
      <h1>{t('Maintenance')}</h1>
      <div className="Maintenance__filters">
        <Input
          type="multiselect"
          size="m"
          label={t('Filter by fleet')}
          options={fleetOptions}
          value={fleets}
          onInput={(val) => setFleets(val)}
        />
        <Input
          type="multiselect"
          size="m"
          label={t('Mobiles')}
          options={mobileOptions}
          value={mobiles}
          onInput={(val) => setMobiles(val)}
        />
        <div className="Maintenance__filters-buttons">
          <Button
            size="m"
            type="primary"
            label={t('Submit')}
            onClick={() => refetch()} // Temporary
          />
          <Button
            size="m"
            type="tertiary"
            label={t('Clear filters')}
            onClick={() => clearFilters()}
          />
        </div>
      </div>
      <div className="Maintenance__table">
        <Table
          isLoading={isLoading}
          isExpandable={true}
          showSearch={true}
          tableHeader={mappedHeader}
          rows={rows}
        />
      </div>
    </div>
  )
}

export default Maintenance
